import { useQuery } from '@tanstack/react-query'
import { MapPin, Home, Briefcase, Star, Phone } from 'lucide-react'
import { addressService } from '../services/api'
import { Breadcrumbs } from '../components/ui'

function AddressTypeIcon({ type }) {
  if (type === 'Home')   return <Home size={14} />
  if (type === 'Office') return <Briefcase size={14} />
  return <MapPin size={14} />
}

export default function Addresses() {
  const { data: addresses = [], isLoading } = useQuery({
    queryKey: ['addresses'],
    queryFn: addressService.getAll,
  })

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in">
      <Breadcrumbs items={[
        { href: '/', label: 'Home' },
        { href: '/account', label: 'Account' },
        { label: 'Addresses' },
      ]} />

      <div className="mb-6">
        <h1 className="section-title flex items-center gap-2">
          <MapPin size={22} className="text-primary" /> Delivery Addresses
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Your registered delivery addresses
        </p>
      </div>

      {/* Info notice */}
      <div className="mb-5 p-4 rounded-xl bg-primary/5 border border-primary/20 flex items-start gap-3">
        <MapPin size={16} className="text-primary mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-display font-semibold text-gray-800 dark:text-white">Delivery address policy</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            As a licensed bulk distributor, deliveries can only be made to your registered address on file.
            To update your delivery address, please contact us on{" "}
            <a href="tel:0123231520" className="text-primary hover:underline font-medium">012 323 1520</a>.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="card h-28 animate-pulse" />)}
        </div>
      ) : addresses.length === 0 ? (
        <div className="card text-center py-16">
          <MapPin size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="font-display font-semibold text-gray-500 dark:text-gray-400 mb-1">No address on file</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">Contact us to register your delivery address</p>
          <a
            href="tel:0123231520"
            className="mt-4 inline-flex items-center gap-2 btn-primary py-2 px-5 text-sm"
          >
            <Phone size={15} /> Call Us
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.map((addr, idx) => (
            <div key={addr.id} className="card p-5 flex items-start gap-4">
              <div className="mt-0.5 w-9 h-9 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-red-400 flex-shrink-0">
                <AddressTypeIcon type={addr.address_type} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-display font-semibold uppercase tracking-wide text-primary dark:text-red-400">
                    {addr.address_type || 'Address'}
                  </span>
                  {idx === 0 && (
                    <span className="inline-flex items-center gap-1 text-xs bg-gold/15 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full font-medium">
                      <Star size={10} fill="currentColor" /> Default
                    </span>
                  )}
                </div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">{addr.address_line1}</p>
                {addr.address_line2 && <p className="text-sm text-gray-500 dark:text-gray-400">{addr.address_line2}</p>}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {[addr.city, addr.postal_code].filter(Boolean).join(', ')}
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500">{addr.country}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
